import { useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { toast } from "react-toastify";

import { styles } from "../form";
import { useI18n } from "@/i18n/I18nProvider";
import { api } from "@/utils/Axios";
import { ArCampaignSchema } from "@/utils/ar_schema";
import { EnCampaignSchema } from "@/utils/en_schema";
import { colors } from "@/utils/const";

const TOAST_OPTIONS = {
  position: "top-center",
  autoClose: 3000,
  hideProgressBar: true,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  progress: undefined,
  theme: "colored",
};

/* Only these variants collect a preferred calling time. */
const TYPES_WITH_CALLING_TIME = ["sms", "exclusive-offers", "catalogue"];

const collectsCallingTime = (type) => TYPES_WITH_CALLING_TIME.includes(type);

/* Products the catalogue variant does not offer. */
const PRODUCTS_HIDDEN_FROM_CATALOGUE = new Set([
  "GreenFinance",
  "shopping",
  "Leasing",
  "Factoring",
  "Maintenance",
]);

/* Option ids reserved for the business audience, hidden from lead forms. */
const BUSINESS_ONLY_OPTION_IDS = new Set(["3", "4"]);

const MANASIK_CATALOGUE_TITLE = {
  ar: "السفر والسياحة",
  en: "Travel and Tourism",
};

const BASE_INITIAL_VALUES = {
  name: "",
  email: "",
  mobilePhone: "",
  products: "",
};

const FIELD_HEIGHT_SX = { height: "45px" };

const SELECT_INPUT_SX = { height: "45px", color: "grey", lineHeight: "2" };

const CALLING_TIME_SELECT_PROPS = {
  MenuProps: { PaperProps: { style: { marginTop: 1, maxHeight: 250 } } },
};

export default function Campaign({
  type,
  title,
  subTitle,
  source,
  isCatalogueStyle,
  isCenter,
}) {
  const { t, locale } = useI18n();
  const [loading, setLoading] = useState(false);

  const withCallingTime = collectsCallingTime(type);
  const menuItemSx = { direction: locale === "ar" ? "rtl" : "ltr" };

  const onSubmit = async (values, actions) => {
    setLoading(true);

    api
      .post(`/services/lead-form/news-letter`, {
        name: values.name,
        email: values.email,
        mobile: values.mobilePhone,
        type: values.products,
        source,
        ...(withCallingTime
          ? { preferredCallingTime: values.preferredCallingTime }
          : {}),
      })
      .then(() => {
        toast.success(t.formMsg, TOAST_OPTIONS);
      })
      .catch(() => {
        toast.error(t.formMsgFail, TOAST_OPTIONS);
      })
      .finally(() => {
        actions.resetForm();
        setLoading(false);
      });
  };

  const initialValues = useMemo(
    () =>
      collectsCallingTime(type)
        ? { ...BASE_INITIAL_VALUES, preferredCallingTime: "" }
        : BASE_INITIAL_VALUES,
    [type],
  );

  const arSchema = useMemo(() => ArCampaignSchema(type), [type]);
  const enSchema = useMemo(() => EnCampaignSchema(type), [type]);

  const { values, errors, touched, handleBlur, handleSubmit, handleChange } =
    useFormik({
      initialValues,
      validationSchema: locale === "en" ? enSchema : arSchema,
      onSubmit,
      enableReinitialize: true, // مهم عشان initialValues تتغير مع type
    });

  /*
   * Formik re-renders on every keystroke, so the option list is derived once
   * per type/locale rather than rebuilt on each character typed.
   */
  const productOptions = useMemo(() => {
    const isExclusiveOffers = type === "exclusive-offers";
    const isCatalogue = type === "catalogue";

    const list = isExclusiveOffers
      ? t.exclusiveOffersList || []
      : t.business_profile_list || [];

    const isAvailable = (option) => {
      // متطبقش فلتر optionId على العروض الحصرية
      if (!isExclusiveOffers && BUSINESS_ONLY_OPTION_IDS.has(option.optionId)) {
        return false;
      }

      return !(isCatalogue && PRODUCTS_HIDDEN_FROM_CATALOGUE.has(option.value));
    };

    return list.filter(isAvailable).map((option) =>
      isCatalogue && option.value === "Manasik"
        ? { ...option, title: MANASIK_CATALOGUE_TITLE[locale === "ar" ? "ar" : "en"] }
        : option,
    );
  }, [t.exclusiveOffersList, t.business_profile_list, type, locale]);

  // Drop a selected product that the current variant no longer offers.
  useEffect(() => {
    if (!values.products) return;

    const stillOffered = productOptions.some(
      (option) => option.value === values.products,
    );

    if (!stillOffered) {
      handleChange({ target: { name: "products", value: "" } });
    }
  }, [productOptions, values.products, handleChange]);

  const formSection = (
    <Box
      component="form"
      onSubmit={handleSubmit}
      autoComplete="off"
      noValidate
      sx={{
        display: "flex",
        flexWrap: "wrap",
        margin: "0 auto",
        gap: isCatalogueStyle ? 1 : 2,
        mb: 5,
        justifyContent: "space-between",
        backgroundColor: isCatalogueStyle ? "white" : colors.bg_grey,
        borderRadius: "20px",
        width: isCatalogueStyle
          ? { xs: "300px", sm: "100%" }
          : isCenter
            ? { xs: "100%", sm: "60%" }
            : { xs: "300px", sm: "500px" },
        padding: "20px",
      }}
    >
      <Box sx={styles.inputWrapper} style={{ width: "100%" }}>
        <InputLabel sx={styles.label} htmlFor="name">
          {t.form_labels.full_name}
        </InputLabel>
        <TextField
          value={values.name}
          onChange={handleChange}
          id="name"
          type="text"
          onBlur={handleBlur}
          error={Boolean(touched.name && errors.name)}
          sx={styles.input}
          InputProps={{ sx: FIELD_HEIGHT_SX }}
          variant="outlined"
        />
        <Box sx={styles.helperText}>{touched.name && errors.name}</Box>
      </Box>

      <Box sx={styles.inputWrapper} style={{ width: "200px" }}>
        <InputLabel sx={styles.label} htmlFor="email">
          {t.form_labels.email_adress}
        </InputLabel>
        <TextField
          value={values.email}
          onChange={handleChange}
          id="email"
          type="email"
          onBlur={handleBlur}
          error={Boolean(touched.email && errors.email)}
          sx={styles.input}
          InputProps={{ sx: FIELD_HEIGHT_SX }}
          variant="outlined"
        />
        <Box sx={styles.helperText}>{touched.email && errors.email}</Box>
      </Box>

      <Box sx={styles.inputWrapper} style={{ width: "200px" }}>
        <InputLabel sx={styles.label} htmlFor="mobilePhone">
          {t.form_labels.phone_number}
        </InputLabel>
        <TextField
          value={values.mobilePhone}
          onChange={(event) => {
            if (event.target.value.length <= 11) {
              handleChange(event);
            }
          }}
          id="mobilePhone"
          type="text"
          onBlur={handleBlur}
          error={Boolean(touched.mobilePhone && errors.mobilePhone)}
          sx={styles.input}
          InputProps={{ sx: FIELD_HEIGHT_SX }}
          variant="outlined"
        />
        <Box sx={styles.helperText}>
          {touched.mobilePhone && errors.mobilePhone}
        </Box>
      </Box>

      <Box sx={styles.inputWrapper} style={{ width: "100%" }}>
        <InputLabel sx={styles.label} htmlFor="products">
          {type === "exclusive-offers"
            ? t.form_labels.offers_label
            : t.form_labels.products}
        </InputLabel>

        <TextField
          value={values.products || "default"}
          onChange={handleChange}
          name="products"
          id="products"
          type="text"
          onBlur={handleBlur}
          error={Boolean(touched.products && errors.products)}
          sx={styles.input}
          select
          style={FIELD_HEIGHT_SX}
          InputProps={{ sx: SELECT_INPUT_SX }}
        >
          <MenuItem sx={menuItemSx} disabled value="default">
            {type === "exclusive-offers"
              ? t.form_labels.offers_placeholder
              : t.form_labels.products_placeholder}
          </MenuItem>

          {productOptions.map((option, index) => (
            <MenuItem sx={menuItemSx} key={index} value={option.value}>
              {option.title}
            </MenuItem>
          ))}
        </TextField>

        <Box sx={styles.helperText}>{touched.products && errors.products}</Box>
      </Box>

      {withCallingTime && (
        <Box sx={styles.inputWrapper} style={{ width: "100%" }}>
          <InputLabel sx={styles.label} htmlFor="preferredCallingTime">
            {t.form_labels.preferredCallingTime}
          </InputLabel>
          <TextField
            value={values.preferredCallingTime || "default"}
            onChange={handleChange}
            name="preferredCallingTime"
            id="preferredCallingTime"
            type="text"
            onBlur={handleBlur}
            error={Boolean(
              touched.preferredCallingTime && errors.preferredCallingTime,
            )}
            sx={styles.input}
            select
            style={FIELD_HEIGHT_SX}
            InputProps={{ sx: SELECT_INPUT_SX }}
            SelectProps={CALLING_TIME_SELECT_PROPS}
          >
            <MenuItem disabled value="default" sx={menuItemSx}>
              {t.form_labels.time_placholder}
            </MenuItem>

            {t.preferredCallingTime.map((time, index) => (
              <MenuItem key={index} value={time} sx={menuItemSx}>
                {time}
              </MenuItem>
            ))}
          </TextField>
          <Box sx={styles.helperText}>
            {touched.preferredCallingTime && errors.preferredCallingTime}
          </Box>
        </Box>
      )}

      <Button
        aria-label="campaign submit button"
        disabled={loading}
        type="submit"
        sx={styles.btn}
        style={{
          backgroundColor: colors.blue,
          border: `1px solid #74357D`,
        }}
      >
        {/*
         * A span, not a heading: the label sits inside a button, and every
         * heading style is overridden to `inherit` here anyway.
         */}
        <Typography
          component="span"
          sx={{
            margin: 0,
            fontSize: "inherit",
            fontWeight: "inherit",
            lineHeight: "inherit",
            color: "inherit",
          }}
        >
          {t.form_labels.submit}
        </Typography>
      </Button>
    </Box>
  );

  return (
    <Box
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: isCatalogueStyle || isCenter ? "center" : "start",
      }}
    >
      <Box className="horizontal-safe-margin section-safe-margin-top">
        {!isCenter && (
          <Typography
            component="h1"
            variant="h5"
            sx={{
              color: isCatalogueStyle ? colors.navy : colors.orange,
              marginBottom: "15px",
              fontSize: "25px",
              fontWeight: "bold",
              textAlign: isCatalogueStyle && "center",
            }}
          >
            {title}
          </Typography>
        )}
      </Box>

      <Box className="horizontal-safe-margin section-safe-margin-bottom">
        <Typography
          component="h2"
          variant="h5"
          sx={{
            color: colors.orange,
            fontSize: isCenter ? "24px" : "20px",
            textAlign: (isCatalogueStyle || isCenter) && "center",
            fontWeight: isCenter ? "700" : "normal",
            maxWidth: isCenter ? { md: "80%" } : "100%",
            margin: "auto",
          }}
        >
          {subTitle}
        </Typography>
      </Box>

      {isCatalogueStyle ? (
        <Box sx={{ width: { xs: "500px", lg: "650px", xl: "950px" } }}>
          <Box
            sx={{
              overflow: "hidden",
              backgroundImage: {
                xs: "url(https://contact-app-prod.s3.us-east-2.amazonaws.com/contact.eg/form-bg-mobile.png)",
                lg: "url(https://contact-app-prod.s3.us-east-2.amazonaws.com/contact.eg/form-bg.png)",
              },
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              p: { xs: 5, lg: 4, xl: 8 },
            }}
          >
            {formSection}
          </Box>
        </Box>
      ) : (
        formSection
      )}
    </Box>
  );
}
